import {
  CompanyProfile,
  GroupEvaluationProfile,
  RequirementEvaluationProfile,
  Roles,
  Status,
} from '@shared/types'
import {
  MaterialTableProps,
  MTableAction,
  MTableGroupRow,
  MTableBodyRow,
} from '@material-table/core'
import { useRouter } from '@shared/hooks/use-router'
import { useThemeStyles } from '@shared/theme/use-theme-styles'
import { useACL } from '@shared/hooks/use-acl.hook'
import { useStore } from '@shared/hooks/use-store.hook'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { getStatusColor } from '@modules/requirements/requirements.utils'
import { originalIcons } from '@shared/components/icons/icons'
import {
  TABLE_PADDING_COMMON,
  TABLE_PADDING_LEFT,
} from '@shared/components/table/table.consts'
import { colors, Tooltip } from '@mui/material'
import { ShowConditionally } from '@shared/components/show-conditionally.component'
import { Actions, TableData } from '@shared/lib/reducer'
import React from 'react'
import { Table } from '@shared/components/table/table.component'
import { FilterStatusesComponent } from '@modules/requirement/filter-statuses.component'
import { Empty } from '@shared/components/empty.component'
import { ACL } from '@shared/components/acl.component'
import { ProgressButton } from '@shared/components/progress-button.component'
import makeStyles from '@mui/styles/makeStyles'
import { TableStatusIndicatorComponent } from '@modules/requirement/requirement-table/table-status-indicator.component'
import { TableRequirementDescriptionComponent } from '@modules/requirement/requirement-table/table-requirement-description.component'
import { useIsClientBasic } from '@shared/hooks/use-is-client-basic'
import MultilineContainer from '@shared/components/multiline-container'
import { useRequirementMutation } from './use-requirement-mutation'
import { TableAlertIndicatorComponent } from '@modules/requirement/requirement-table/table-alert-indicator.component'

export type TableRequirement = RequirementEvaluationProfile & {
  groupName: string
  groupOrder: number
  colors: Record<string, string>
  no: string
}

export type RegisteredStatus = {
  statusBaseID: number
  statusColor: string
  name: string
}

type Props = {
  auditID?: number
  companyID: number
  isLoading: boolean
  isPreviewMode?: boolean
  trackBy?: string
  actions: MaterialTableProps<TableRequirement>['actions']
  companyProfile: CompanyProfile
  groups: GroupEvaluationProfile[]
  initialScrollPosition?: 'top'
  initiallyCollapsedWithButtons?: boolean
  enableColumnOffset?: boolean
  enableColumnNo?: boolean
  enableColumnOrder?: boolean
  enableColumnStatus?: boolean
  enableDontShow?: boolean
  enableGroupRowVerifiedCount?: boolean
  enableShowNotActive?: boolean
  currentScopeID?: number
  statuses: Status[]
  isActive?: boolean
  hideAdditionalComponent?: boolean
}

export const NOT_VERIFIED_STATUS = 5
const DONT_SHOW_LABEL = 'Nie pokazuj'

export const RequirementsTable = (props: Props) => {
  const { statuses, groups, auditID, companyID, currentScopeID, isActive } =
    props
  const classes = useStyles()
  const themeClasses = useThemeStyles()
  const { is } = useACL()
  const { state, dispatch } = useStore()
  const [filteredData, setFilteredData] = useState<TableRequirement[] | null>(
    null
  )
  const [activeFilters, setActiveFilters] = useState<number[]>([])
  const [isHideFloating, setIsHideFloating] = useState(true)
  const [registeredStatuses, setRegisteredStatuses] =
    useState<RegisteredStatus[]>()
  const triggerRef = useRef<HTMLDivElement>(null)
  const { hideGroup } = useRequirementMutation(companyID, auditID, statuses)

  const isClientBasic = useIsClientBasic()
  const { query, navigate, location } = useRouter()
  const focusRequirementID = (query as any).focusRequirementID

  useEffect(() => {
    if (focusRequirementID) {
      // Small timeout to allow table to render rows
      setTimeout(() => {
        const element = document.getElementById(`req-${focusRequirementID}`)
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' })

          // Clear query param to avoid scrolling on subsequent renders
          const newQuery = new URLSearchParams(location.search)
          newQuery.delete('focusRequirementID')
          navigate(`${location.pathname}?${newQuery.toString()}`, { replace: true })
        }
      }, 500)
    }
  }, [focusRequirementID, navigate, location])

  const data = useMemo(() => {
    return groups.flatMap((group, groupIndex) =>
      group.requirements.map((requirement, requirementIndex) => ({
        ...requirement,
        groupName: group.name,
        groupOrder: groupIndex + 1,
        no: `${groupIndex + 1}.${requirementIndex + 1}`,
        colors: {
          client: getStatusColor(
            statuses,
            requirement.clientStatus?.statusBaseId
          ),
          auditor: getStatusColor(
            statuses,
            requirement.auditorStatus?.statusBaseId
          ),
        },
      }))
    )
  }, [groups, statuses])

  const auditScopeData = useMemo(
    () =>
      state.requirementTableData.find(
        (data: TableData) =>
          data.auditID === props.auditID &&
          data.scopeID === props.currentScopeID
      ),
    [props.auditID, props.currentScopeID, state.requirementTableData]
  )

  useEffect(() => {
    if (auditScopeData !== undefined) {
      setActiveFilters(auditScopeData.filters)
    }
  }, [auditScopeData, groups])

  useEffect(() => {
    let updatedData = data
    if (auditScopeData?.filters.length > 0) {
      updatedData = updatedData.filter((requirement) =>
        auditScopeData?.filters.includes(
          (requirement as TableRequirement)?.auditorStatus?.statusBaseId
        )
      )
    }

    if (state.isDraftFilterActive) {
      updatedData = updatedData.filter(
        (requirement) => (requirement as TableRequirement).isDraft
      )
    }

    setFilteredData(updatedData)
  }, [auditScopeData?.filters, data, state.isDraftFilterActive])

  useEffect(() => {
    dispatch({
      type: Actions.SET_REQUIREMENT_TABLE_DATA,
      payload: {
        auditID: props.auditID,
        filters: activeFilters,
        scopeID: props.currentScopeID || undefined,
      },
    })
  }, [activeFilters, dispatch, props.auditID, props.currentScopeID])

  useEffect(() => {
    const existingStatuses: RegisteredStatus[] = (
      data as TableRequirement[]
    ).reduce<RegisteredStatus[]>((current, el: TableRequirement) => {
      if (
        !el.auditorStatus ||
        !el.colors ||
        current.some(
          (item) => item.statusBaseID === el.auditorStatus?.statusBaseId
        )
      ) {
        return current
      }

      return [
        ...current,
        {
          statusBaseID: el.auditorStatus.statusBaseId,
          statusColor: el.colors.auditor,
          name:
            (statuses || []).find(
              ({ statusID }) => el.auditorStatus?.statusBaseId === statusID
            )?.name || '',
        },
      ]
    }, [])

    setRegisteredStatuses(existingStatuses)
  }, [data, statuses])

  const groupList = useMemo<string[]>(
    () => groups.map((item) => item.name),
    [groups]
  )

  const expandAll = useCallback(() => {
    dispatch({ type: Actions.ADD_EXPANDED_LIST, payload: groupList })
  }, [dispatch, groupList])

  const collapseAll = useCallback(() => {
    dispatch({ type: Actions.REMOVE_EXPANDED_LIST, payload: groupList })
  }, [dispatch, groupList])

  const onDontShowGroup = useCallback(
    (group: GroupEvaluationProfile) => {
      hideGroup({ groupId: group.id, scopeId: group.scopeId })
    },
    [hideGroup]
  )

  const columns = useMemo(
    () =>
      [
        props.enableColumnOffset
          ? {
            field: 'offset',
            width: '1%',
            cellStyle: {
              ...TABLE_PADDING_COMMON,
            },
          }
          : false,
        props.enableColumnOrder
          ? {
            field: 'order',
            width: '4%',
            cellStyle: {
              ...TABLE_PADDING_COMMON,
              ...TABLE_PADDING_LEFT,
            },
          }
          : false,
        props.enableColumnNo
          ? {
            field: 'no',
            width: '5%',
            cellStyle: {
              ...TABLE_PADDING_COMMON,
              ...TABLE_PADDING_LEFT,
              textAlign: 'center',
            },
          }
          : false,
        {
          title: '',
          field: 'groupName',
          defaultGroupOrder: 0,
          customSort: () => { },
          cellStyle: {
            ...TABLE_PADDING_COMMON,
          },
        },
        props.enableColumnStatus
          ? {
            field: 'color',
            render: (requirement: TableRequirement) => {
              return (
                <TableStatusIndicatorComponent
                  requirement={requirement}
                  isPreviewMode={props.isPreviewMode}
                />
              )
            },
            cellStyle: {
              ...TABLE_PADDING_COMMON,
              ...TABLE_PADDING_LEFT,
              paddingRight: 8,
            },
          }
          : false,
        {
          field: 'alert',
          render: (requirement: TableRequirement) => {
            if (requirement.alert?.acceptanceStatus === 0) {
              return (
                <TableAlertIndicatorComponent
                  requirement={requirement}
                  isActive={!!props.isActive}
                />
              )
            }
          },
        },
        {
          field: 'name',
          width: '15%',
          cellStyle: {
            ...TABLE_PADDING_COMMON,
          },
        },
        {
          field: 'description',
          width: '75%',
          render: (requirement: TableRequirement) => (
            <MultilineContainer>
              <TableRequirementDescriptionComponent requirement={requirement} />
            </MultilineContainer>
          ),
          cellStyle: {
            ...TABLE_PADDING_COMMON,
            ...TABLE_PADDING_LEFT,
          },
        },
      ].filter(Boolean) as MaterialTableProps<TableRequirement>['columns'],
    [
      props.enableColumnOffset,
      props.enableColumnOrder,
      props.enableColumnNo,
      props.enableColumnStatus,
    ]
  )

  const renderFilters = useCallback(
    (type: 'normal' | 'floating') => {
      if (registeredStatuses && !props.hideAdditionalComponent) {
        return (
          <FilterStatusesComponent
            registeredStatuses={registeredStatuses}
            setFilteredData={(value) =>
              setFilteredData(value as TableRequirement[] | null)
            }
            data={data}
            activeFilters={activeFilters}
            setActiveFilters={setActiveFilters}
            initiallyCollapsedWithButtons={props.initiallyCollapsedWithButtons}
            expandAll={expandAll}
            collapseAll={collapseAll}
            isExpanded={state.expandedValues.length}
            isPreviewMode={props.isPreviewMode}
            hideFilters={isClientBasic || !props.isActive}
            type={type}
            currentScopeID={currentScopeID}
            isActive={isActive}
          />
        )
      }
      return null
    },
    [
      registeredStatuses,
      data,
      activeFilters,
      props.isPreviewMode,
      isClientBasic,
      state,
    ]
  )

  const options = useMemo(
    () => ({
      grouping: true,
      groupRowSeparator: ' ',
      rowStyle: (data: unknown) => {
        const rowData = data as TableRequirement
        const defaultStyle = { fontSize: 14 }

        if (rowData.isDraft && !isClientBasic) {
          return {
            ...defaultStyle,
            backgroundColor: colors.grey['200'],
          }
        }

        if (
          rowData.isOutdated &&
          is([Roles.Client, Roles.ClientEditor, Roles.ClientAdministrator])
        ) {
          return {
            ...defaultStyle,
            backgroundColor: colors.red['200'],
          }
        }

        return defaultStyle
      },
    }),
    []
  )

  const pageSize = groups.length < 100 ? groups.length : 100

  const renderGroupRow = useCallback(
    (groupRowProps: any) => {
      const currentValue = groupRowProps.groupData.value
      const currentGroup = groups.find((group) => group.name === currentValue)
      const groupOrder =
        data.find((item) => item.groupName === currentValue)?.groupOrder ||
        currentGroup?.order

      if (!currentGroup) {
        return (
          <MTableGroupRow {...groupRowProps} className={classes.groupRow} />
        )
      }

      const verifiedTotal = currentGroup.requirements.length
      const verified = currentGroup.requirements.reduce(
        (count: number, requirement: RequirementEvaluationProfile) =>
          count +
          (requirement.auditorStatus?.statusBaseId !== NOT_VERIFIED_STATUS &&
            requirement.auditorStatus?.statusBaseId !== 8 &&
            !requirement.isDraft
            ? 1
            : 0),
        0
      )

      const newProps = {
        ...groupRowProps,
        groupData: {
          ...groupRowProps.groupData,
          isExpanded: props.initiallyCollapsedWithButtons
            ? state.expandedValues?.includes(currentValue)
            : !state.expandedValues?.includes(currentValue),
          value: (
            <>
              <div className={classes.groupRow}>
                <span
                  style={{
                    paddingRight: 20,
                  }}
                >
                  {groupOrder}.
                </span>
                <span style={{ flex: 1 }}>{groupRowProps.groupData.value}</span>
                <ShowConditionally when={props.enableGroupRowVerifiedCount}>
                  <span>
                    {verified}/{verifiedTotal} zweryfikowano
                  </span>
                </ShowConditionally>
                <ShowConditionally when={props.enableDontShow}>
                  <ACL allowedRoles={[Roles.Admin, Roles.Auditor]}>
                    <Tooltip title={DONT_SHOW_LABEL}>
                      <ProgressButton
                        variant="outlined"
                        className={classes.dontShowButton}
                        onClick={() => onDontShowGroup(currentGroup)}
                      >
                        <originalIcons.VisibilityOff />
                      </ProgressButton>
                    </Tooltip>
                  </ACL>
                </ShowConditionally>
              </div>
            </>
          ),
        },
      }

      return <MTableGroupRow {...newProps} className={classes.groupRow} />
    },
    [
      groups,
      state.expandedValues,
      props.initiallyCollapsedWithButtons,
      props.enableGroupRowVerifiedCount,
      props.enableDontShow,
    ]
  )

  const tableHeaderRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const scrollContainer = document.getElementById('container')

    if (!scrollContainer) {
      return
    }

    const checkHeaderVisibility = () => {
      if (tableHeaderRef.current) {
        const rect = tableHeaderRef.current.getBoundingClientRect()
        const containerRect = scrollContainer.getBoundingClientRect()
        const isHeaderVisible = rect.top >= containerRect.top
        setIsHideFloating(isHeaderVisible)
      }
    }

    checkHeaderVisibility()
    scrollContainer.addEventListener('scroll', checkHeaderVisibility)

    return () => {
      scrollContainer.removeEventListener('scroll', checkHeaderVisibility)
    }
  }, [])

  const renderTableHeader = () => (
    <div
      ref={tableHeaderRef}
      style={{ position: 'relative', height: '1px', width: '100%' }}
    ></div>
  )

  return (
    <div className={classes.tableContainer}>
      {renderTableHeader()}
      <div className={themeClasses.tableContainer}>
        <Table<TableRequirement>
          tableRef={triggerRef}
          pageSize={pageSize}
          title="Wymagania"
          trackScroll
          // initialScrollPosition="top"
          trackPanelsBy={props.trackBy}
          isLoading={props.isLoading}
          options={options}
          data={filteredData ?? data}
          columns={columns}
          actions={props.actions}
          additionalComponent={
            !isHideFloating
              ? renderFilters('floating')
              : renderFilters('normal')
          }
          components={{
            Groupbar: Empty,
            Header: Empty,
            Action: (actionProps) => <MTableAction {...actionProps} />,
            GroupRow: renderGroupRow,
            Row: (rowProps) => (
              <div id={`req-${rowProps.data.id}`} style={{ display: 'contents' }}>
                <MTableBodyRow {...rowProps} />
              </div>
            ),
          }}
        />
      </div>
    </div>
  )
}

const useStyles = makeStyles((theme) => ({
  deprecatedWarning: {
    marginLeft: 10,
    color: theme.palette.error.main,
  },
  colorCircle: {
    width: '25px',
    height: '25px',
    borderRadius: '50%',
  },
  groupRow: {
    display: 'inline-flex',
    justifyContent: 'space-between',
    width: 'calc(100% - 50px)',
    position: 'relative',
    backgroundColor: theme.palette.common.white,
    fontSize: 14,
  },
  filterCheckboxForm: {
    flex: 1,
    marginLeft: '10px',
  },
  dontShowButton: {
    height: 20,
    marginLeft: theme.spacing(2),
    border: 'none',
  },
  tableContainer: {
    position: 'relative',
    '& .MuiTableCell-paddingNone:empty': {
      width: '0 !important',
    },
  },
  progressOverlay: {
    color: '#fff',
    zIndex: theme.zIndex.drawer + 1,
  },
}))
